import { FaroButtonSpinner } from "@components/common/button/faro-button-spinner";
import { SphereIconButton } from "@components/common/sphere-icon-button";
import { UserAvatarWithMenu } from "@components/user-more-info-menu/user-avatar-with-menu";
import { useMarkupContext } from "@context-providers/markup/markup-context";
import { useProjectMarkupUpdate } from "@hooks/project-markups/use-project-markup-update";
import { useMembers } from "@hooks/use-members";
import { Box } from "@mui-wrapper";
import { MarkupAssigneeEditable } from "@pages/project-details/project-markups/editable/markup-assignee-editable";
import { getMarkupAssigneeMember, getMarkupAssigneeName } from "@pages/project-details/project-markups/markup-assignee-utils";
import { removeIElements } from "@store/i-elements/i-elements-slice-thunk";
import { useAppDispatch } from "@store/store-helper";
import { CloseIcon, Text, UserNewIcon } from "@flat-ui";
import { withEllipsis } from "@styles/common-styles";
import { memo, useMemo, useState } from "react";
import { PALETTE_VARS } from "@styles/palette-vars";
/** Used to control the visibility on hover */
const MARKUP_ASSIGNEE_EDIT_ICON_CLASS = "assignee_edit";

/**
 * Component responsible for displaying and managing the assignee information
 * for an annotation markup within a project.
 *
 * This component renders the assignee's avatar, name, and edit/delete functionality
 * if the current user has appropriate permissions. It also provides an editable popover
 * for modifying the assignee details upon user interaction.
 *
 * @param {Props} props - The props for the MarkupAssigneeComponent.
 * @returns {JSX.Element} A JSX element representing the MarkupAssigneeComponent.
 */
function MarkupAssigneeComponent({
  markup,
  shouldHideWhiteRim = true,
  isSidePanelOpen = false
}) {
  const {
    projectId,
    updateSelectedMarkupId,
    hasPermissionToEditMarkup,
    setIsMarkupUpdating
  } = useMarkupContext();
  const {
    removeMember
  } = useProjectMarkupUpdate({
    projectId
  });
  const dispatch = useAppDispatch();
  const {
    companyMembers,
    projectMembers
  } = useMembers();
  const [anchorEl, setAnchorEl] = useState(null);
  const [isSpinnerLoading, setIsSpinnerLoading] = useState(false);

  /** Filter the members to remove all the members viewers for the dropdown */
  const filterMembers = useMemo(() => projectMembers.filter(projectMember => projectMember.role !== "RESOURCE_VIEWER"), [projectMembers]);
  const assigneeMember = useMemo(() => {
    return getMarkupAssigneeMember({
      assignee: markup.assignee,
      companyMembers,
      projectMembers: filterMembers
    });
  }, [markup, companyMembers, filterMembers]);
  const markupAssignName = useMemo(() => getMarkupAssigneeName({
    assignee: markup.assignee,
    companyMembers,
    projectMembers: filterMembers
  }), [markup, companyMembers, filterMembers]);
  const assigneeName = useMemo(() => {
    return hasPermissionToEditMarkup ? markupAssignName ?? <Box sx={{
      pl: isSidePanelOpen ? "3px" : 0
    }}>
            <UserNewIcon sx={{
        width: "18px",
        height: "18px",
        color: "text.secondary"
      }} />
          </Box> : markupAssignName || "-";
  }, [hasPermissionToEditMarkup, isSidePanelOpen, markupAssignName]);

  /**
   * Handles the removal of a member assigned to an annotation markup.
   *
   * @param event The mouse event triggered by the button click.
   * @returns A promise that resolves once the member removal process is completed.
   */
  async function handleRemoveMember(event) {
    event.stopPropagation();
    if (markup.assignee) {
      setIsSpinnerLoading(true);
      setIsMarkupUpdating(true);
      if (await removeMember(markup.assignee.id)) {
        void dispatch(removeIElements([markup.assignee]));
      }
      setIsSpinnerLoading(false);
      setIsMarkupUpdating(false);
    }
  }

  /**
   * Handles closing behavior when clicking away from the component.
   * Resets the anchor element and updates the selected markup to undefined.
   */
  function closeAssigneeDropdown() {
    updateSelectedMarkupId(undefined);
    setAnchorEl(null);
  }

  /**
   * Handles the event when opening the assignee dropdown menu.
   * Stops the event propagation to prevent unintended interactions
   *
   * @param event The mouse event triggering the dropdown menu opening.
   */
  function openAssigneeDropdown(event_0) {
    if (hasPermissionToEditMarkup) {
      event_0.stopPropagation();
      updateSelectedMarkupId(markup.id);
      setAnchorEl(event_0.currentTarget);
    }
  }
  return <>
      <Box component="div" sx={{
      display: "flex",
      alignItems: "center",
      width: "100%",
      height: "100%",
      px: isSidePanelOpen ? 0 : "8px",
      gap: "10px",
      ...(hasPermissionToEditMarkup && {
        cursor: "pointer",
        borderBottom: anchorEl ? `2px solid ${PALETTE_VARS.primary.light}` : "1px solid transparent",
        backgroundColor: anchorEl ? "background.paper" : undefined,
        "&:hover": {
          [`& .${MARKUP_ASSIGNEE_EDIT_ICON_CLASS}`]: {
            visibility: anchorEl ? "hidden" : "visible"
          },
          borderBottom: !anchorEl ? `1px solid ${PALETTE_VARS.text.primary}` : ""
        }
      })
    }} onClick={openAssigneeDropdown}>
        {assigneeMember && <UserAvatarWithMenu member={assigneeMember} shouldHideWhiteRim={shouldHideWhiteRim} size={isSidePanelOpen ? "x-small" : "small"} />}

        <Text variant="bodyS" component="var" sx={{
        ...withEllipsis
      }}>
          {assigneeName}
        </Text>

        <Box sx={{
        marginLeft: "auto"
      }}>
          {isSpinnerLoading && <FaroButtonSpinner />}

          {assigneeMember && hasPermissionToEditMarkup && !isSpinnerLoading && <SphereIconButton component={CloseIcon} buttonSize="24px" iconSize="16px" color={PALETTE_VARS.text.disabled} onClick={handleRemoveMember} iconButtonProps={{
          className: MARKUP_ASSIGNEE_EDIT_ICON_CLASS,
          sx: {
            visibility: "hidden"
          }
        }} />}
        </Box>
      </Box>

      {anchorEl && hasPermissionToEditMarkup && <MarkupAssigneeEditable anchorEl={anchorEl} closeAssigneeDropdown={closeAssigneeDropdown} setIsSpinnerLoading={setIsSpinnerLoading} projectMembers={filterMembers} markup={markup} assigneeMember={assigneeMember} />}
    </>;
}
export const MarkupAssignee = memo(MarkupAssigneeComponent);